import { Button, DrawerBody, DrawerFooter, Icon, SubmitButton, TextField } from "@dgs/core";
import { FieldArray, Formik } from "formik";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EmailTypeSelectField } from "../../contacts/selects/EmailTypeSelectField";
import { PhoneTypeSelectField } from "../../contacts/selects/PhoneTypeSelectField";
import { ContactEmailType, ContactPhoneType } from "../../shared/types";
import { isValidEmail } from "../../shared/utils/email";
import { AdditionalList, AdditionalListItem, EmptyAdditionalList } from "./AdditionalList";
import { CreateContactStepForm, DrawerForm, FormActions } from "./Form";

interface AdditionalEmailFormValues {
	email: string;
	type: ContactEmailType;
}

const emptyAdditionalEmailFormValues: AdditionalEmailFormValues = {
	email: "",
	type: "work",
};

interface AdditionalPhoneNumberFormValues {
	phoneNumber: string;
	type: ContactPhoneType;
}

const emptyAdditionalPhoneNumberFormValues: AdditionalPhoneNumberFormValues = {
	phoneNumber: "",
	type: "workLandline",
};

export interface AdditionalFormValues {
	emails: AdditionalEmailFormValues[];
	phoneNumbers: AdditionalPhoneNumberFormValues[];
}

export const emptyAdditionalFormValues: AdditionalFormValues = {
	emails: [],
	phoneNumbers: [],
};

interface Props extends CreateContactStepForm<AdditionalFormValues> {
	onGoBack: () => void;
}

const Divider = styled.hr`
	margin: ${({ theme }) => theme.spacing(6)} 0;
	border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grey[20]};
`;

export const AdditionalForm: FC<Props> = ({ onGoBack, initialValues, onSubmit }) => {
	const { t } = useTranslation();

	const validate = (values: AdditionalFormValues) => {
		const errors: { [K in keyof AdditionalFormValues]?: any } = {};

		values.emails.forEach((email, index) => {
			if (!email.email) {
				errors.emails = { ...(errors.emails || {}), [index]: { email: t("Required field") } };
			} else if (!isValidEmail(email.email)) {
				errors.emails = { ...(errors.emails || {}), [index]: { email: t("Invalid email address") } };
			}
		});

		values.phoneNumbers.forEach((email, index) => {
			if (!email.phoneNumber) {
				errors.phoneNumbers = { ...(errors.phoneNumbers || {}), [index]: { phoneNumber: t("Required field") } };
			}
		});

		return errors;
	};

	return (
		<Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
			<DrawerForm>
				<DrawerBody>
					<FieldArray name="emails">
						{(props) => (
							<>
								<AdditionalList>
									{(props.form.values as AdditionalFormValues).emails.map((_, index) => (
										<AdditionalListItem key={index} onDelete={() => props.remove(index)}>
											<TextField
												name={`${props.name}[${index}].email`}
												label={t("Additional email address")}
												required
											/>
											<EmailTypeSelectField name={`${props.name}[${index}].type`} label={t("Type")} />
										</AdditionalListItem>
									))}
								</AdditionalList>
								{(props.form.values as AdditionalFormValues).emails.length === 0 && (
									<EmptyAdditionalList>{t("No additional email addresses available.")}</EmptyAdditionalList>
								)}
								<Button
									type="button"
									onClick={() => props.push(emptyAdditionalEmailFormValues)}
									size="small"
									prefix={<Icon $icon="plus" />}
								>
									{t("Add additional email address")}
								</Button>
							</>
						)}
					</FieldArray>
					<Divider />
					<FieldArray name="phoneNumbers">
						{(props) => (
							<>
								<AdditionalList>
									{(props.form.values as AdditionalFormValues).phoneNumbers.map((_, index) => (
										<AdditionalListItem key={index} onDelete={() => props.remove(index)}>
											<TextField
												name={`${props.name}[${index}].phoneNumber`}
												label={t("Additional phone number")}
												required
											/>
											<PhoneTypeSelectField name={`${props.name}[${index}].type`} label={t("Type")} />
										</AdditionalListItem>
									))}
								</AdditionalList>
								{(props.form.values as AdditionalFormValues).phoneNumbers.length === 0 && (
									<EmptyAdditionalList>{t("No additional phone numbers available.")}</EmptyAdditionalList>
								)}
								<Button
									type="button"
									onClick={() => props.push(emptyAdditionalPhoneNumberFormValues)}
									size="small"
									prefix={<Icon $icon="plus" />}
								>
									{t("Add additional phone number")}
								</Button>
							</>
						)}
					</FieldArray>
				</DrawerBody>
				<DrawerFooter>
					<span />
					<FormActions>
						<Button type="button" onClick={onGoBack}>
							{t("Back")}
						</Button>
						<SubmitButton>{t("Save")}</SubmitButton>
					</FormActions>
				</DrawerFooter>
			</DrawerForm>
		</Formik>
	);
};
